import React from "react";
import { FormikProps, getIn } from "formik";
import { SectionShell } from "../../../common/dashboard/SectionShell";
import { FormTextField } from "../../../common/dashboard/FormFields";
import BannerPicker from "../../../common/utilities/BannerPicker";
import LogoPicker from "../../../common/utilities/LogoPicker";
import PhoneNumberFormat from "../../../common/utilities/PhoneNumberFormat";
import { ProvinceAutocomplete } from "../../../common/utilities/ProvincePicker";
import ChamberDescriptionField from "./ChamberDescriptionField";
import { ChamberProfileFormValues } from "./manageChamberProfileModel";
import { googleAddressToMemberFields, SelectedGoogleAddress } from "../../../../utils/googleAddressAutocomplete";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNoun } from "../../../../utils/cmpOrganizationNoun";

type ManageChamberProfileSectionsProps = {
  formik: FormikProps<ChamberProfileFormValues>;
  onLogoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onLogoDelete: () => void;
  onSponsorLogoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSponsorLogoDelete: () => void;
};

type ProfileTextFieldProps = {
  formik: FormikProps<ChamberProfileFormValues>;
  name: string;
  label: React.ReactNode;
  placeholder?: string;
  required?: boolean;
  description?: React.ReactNode;
  type?: string;
  onAddressSelect?: (address: SelectedGoogleAddress) => void;
};

const getProfileFieldError = ({
  formik,
  name,
}: {
  formik: FormikProps<ChamberProfileFormValues>;
  name: string;
}) => {
  const touched = getIn(formik.touched, name) as unknown;
  const error = getIn(formik.errors, name) as unknown;

  return touched && error ? String(error) : undefined;
};

const ProfileTextField = ({
  formik,
  name,
  label,
  placeholder,
  required,
  description,
  type,
  onAddressSelect,
}: ProfileTextFieldProps): React.ReactElement => {
  const rawValue = getIn(formik.values, name) as unknown;

  return (
    <FormTextField
      id={name}
      label={label}
      value={typeof rawValue === "string" ? rawValue : ""}
      onChange={(value) => formik.setFieldValue(name, value)}
      onBlur={() => formik.setFieldTouched(name, true)}
      error={getProfileFieldError({ formik, name })}
      placeholder={placeholder}
      required={required}
      description={description}
      type={type}
      onAddressSelect={onAddressSelect}
      appearance="adminChamber"
      labelClassName="mb-2 block text-base font-semibold leading-5 text-black"
      descriptionClassName="mb-2 text-xs leading-5 text-[#666666]"
    />
  );
};

const twoColumnGridClassName =
  "grid min-w-0 grid-cols-1 gap-x-5 md:grid-cols-2";

type InternalContactField = {
  name: string;
  label: string;
  placeholder: string;
  description?: string;
  type: string;
};

const internalContactFields: readonly InternalContactField[] = [
  {
    name: "supportContactName",
    label: "Chamber Perks App™ Administrator Contact",
    placeholder: "Enter Administrator Contact Name",
    description: "This contact received your chamber credentials",
    type: "text",
  },
  {
    name: "supportContactEmail",
    label: "Chamber Perks App™ Administrator Email",
    placeholder: "Enter Administrator Contact Email",
    description:
      "This will be used as the email username for the chamber credentials",
    type: "email",
  },
  {
    name: "billingContactName",
    label: "Billing Contact Name",
    placeholder: "Enter Billing Contact Name",
    type: "text",
  },
  {
    name: "billingContactEmail",
    label: "Billing Contact Email",
    placeholder: "Enter Billing Contact Email",
    type: "email",
  },
  {
    name: "signingAuthorityContactName",
    label: "Signing Authority Contact Name",
    placeholder: "Enter Signing Authority Contact Name",
    type: "text",
  },
  {
    name: "signingAuthorityContactEmail",
    label: "Signing Authority Contact Email",
    placeholder: "Enter Signing Authority Contact Email",
    type: "email",
  },
] as const;

const socialFields = [
  [
    "facebook",
    "Facebook Business Page URL",
    "Enter Facebook Business Page URL",
  ],
  ["instagram", "Instagram Account URL", "Enter Instagram Account URL"],
  ["threads", "Threads Account URL", "Enter Threads Account URL"],
  ["linkedin", "LinkedIn Page URL", "Enter LinkedIn Page URL"],
  ["twitter", "X (Twitter) Account URL", "Enter X (Twitter) Account URL"],
  ["tiktok", "TikTok Account URL", "Enter TikTok Account URL"],
  ["youtube", "Youtube Channel URL", "Enter Youtube Channel URL"],
] as const;

const chamberTabFields = [
  ["eventsLink", "Events Link URL", "Enter Events Link URL"],
  ["newsletterLink", "Newsletter Link URL", "Enter Newsletter Link URL"],
  ["jobPostingLink", "Job Posting Link URL", "Enter Job Posting Link URL"],
  ["surveyLink", "Survey Link URL", "Enter Survey Link URL"],
  [
    "advocacyWinsLink",
    "Advocacy Wins Link URL",
    "Enter Advocacy Wins Link URL",
  ],
  [
    "memberSignupLink",
    "New Member Sign up Link URL",
    "Enter New Member Sign up Link URL",
  ],
  [
    "callForNominationsLink",
    "Call for Nominations Link URL",
    "Enter Call for Nominations Link URL",
  ],
] as const;

const ManageChamberProfileSections = ({
  formik,
  onLogoUpload,
  onLogoDelete,
  onSponsorLogoUpload,
  onSponsorLogoDelete,
}: ManageChamberProfileSectionsProps): React.ReactElement => {
  const noun = useCmpOrganizationNoun();

  return (
  <div className="mx-auto w-full max-w-[1096px] pb-1">
    <SectionShell
      id="chamber-information"
      title={`${noun.Singular} Information`}
      variant="overlapPill"
      description={
        <>
          The information below was provided in your onboarding form. Update as
          required the name and email of the Chamber Perks App™ dashboard
          administrator. This individual received the dashboard credentials and
          will be identified as the contact on all automated emails to members.{" "}
          <strong>
            This contact information is for internal use only and will not be
            displayed publicly in the app.
          </strong>
        </>
      }
    >
      <div className={twoColumnGridClassName}>
        {internalContactFields.map((field) => (
          <ProfileTextField
            key={field.name}
            formik={formik}
            name={field.name}
            label={field.label}
            placeholder={field.placeholder}
            description={
              field.description
                ? applyCmpOrganizationNoun(field.description, noun)
                : undefined
            }
            type={field.type}
            required
          />
        ))}
      </div>
    </SectionShell>

    <SectionShell
      id="chamber-profile"
      title={`${noun.Singular} Profile on Chamber Perks App™`}
      variant="overlapPill"
      description="Enter the information you want to show in the Chamber Perks App™."
    >
      <div className="mb-5 flex min-w-0 flex-col gap-5 md:flex-row md:gap-6">
        <div className="contents min-w-0 flex-1 md:flex md:flex-col md:gap-5">
          <div className="order-1 min-w-0">
            <ProfileTextField
              formik={formik}
              name="name"
              label={`${noun.Singular} Name`}
              placeholder={`Enter ${noun.Singular} Name`}
              required
            />
          </div>
          <div className="order-3 min-w-0">
            <ProfileTextField
              formik={formik}
              name="website"
              label={`${noun.Singular} Website`}
              placeholder={`Enter ${noun.Singular} Website URL`}
              required
            />
          </div>
        </div>
        <div className="order-2 min-w-0 flex-1 md:order-none">
          <ChamberDescriptionField formik={formik} />
        </div>
      </div>

      <div className={twoColumnGridClassName}>
        <div className="flex min-w-0 items-start gap-3">
          <div className="min-w-0 flex-1">
            <PhoneNumberFormat
              formikVal={formik.values.phoneNumber || ""}
              formikErrors={getProfileFieldError({
                formik,
                name: "phoneNumber",
              })}
              handleChange={formik.handleChange}
              onBlur={() => formik.setFieldTouched("phoneNumber", true)}
              label={`${noun.Singular} Contact Phone`}
              placeholder={`Enter ${noun.Singular} Contact Phone`}
              error={Boolean(
                getProfileFieldError({ formik, name: "phoneNumber" }),
              )}
              helperText={getProfileFieldError({
                formik,
                name: "phoneNumber",
              })}
              required
              appearance="adminChamber"
            />
          </div>
          <div className="w-[100px] shrink-0">
            <ProfileTextField
              formik={formik}
              name="phoneNumberExtension"
              label="Ext."
              placeholder="00000"
            />
          </div>
        </div>
        <ProfileTextField
          formik={formik}
          name="supportLink"
          label={
            <>
              Google Review URL{" "}
              <span className="font-normal text-gray-500">(optional)</span>
            </>
          }
          placeholder="Enter Google Review URL"
        />
      </div>

      <div className={twoColumnGridClassName}>
        <ProfileTextField
          formik={formik}
          name="secondarySupportEmail"
          label={
            <>
              {noun.Singular} Support Email{" "}
              <span className="font-normal text-gray-500">(optional)</span>
            </>
          }
          placeholder={`Enter ${noun.Singular} Contact Email`}
          type="email"
        />
        <ProfileTextField
          formik={formik}
          name="chamberSupportPageUrl"
          label={
            <>
              {noun.Singular} Support Page URL{" "}
              <span className="font-normal text-gray-500">(optional)</span>
            </>
          }
          placeholder={`Enter ${noun.Singular} Support Page URL`}
        />
      </div>

      <div className="mt-1">
        <p className="mb-2 text-base font-semibold text-black">{noun.Singular} Logo</p>
        <p className="mb-[10px] text-sm text-black">
          Logo will be shown in the Chamber Perks App™
        </p>
        <LogoPicker
          logo={formik.values.logo}
          deleteLogo={onLogoDelete}
          base64={onLogoUpload}
        />
      </div>
    </SectionShell>

    <SectionShell
      id="chamber-address"
      title={`${noun.Singular} Address`}
      variant="overlapPill"
      description={
        <>
          Enter the information you want to appear in your {noun.singular} profile and
          map on the Chamber Perks App™. Leave any <strong>fields blank</strong>{" "}
          that you don&apos;t want to appear in the app.
        </>
      }
    >
      <div className={twoColumnGridClassName}>
        <ProfileTextField
          formik={formik}
          name="address.streetAddress"
          label="Street Address or PO Box"
          placeholder="Enter Street Number and Name"
          onAddressSelect={(address) => {
            const selected = googleAddressToMemberFields(address);
            void formik.setFieldValue("address", {
              ...formik.values.address,
              streetAddress: selected.streetAddress,
              city: selected.city,
              province: address.provinceCode || address.province,
              postalCode: selected.postalOrZipCode,
            });
          }}
        />
        <ProfileTextField
          formik={formik}
          name="address.city"
          label="City"
          placeholder="Enter City Name"
        />
        <ProvinceAutocomplete
          value={formik.values.address.province || ""}
          onChange={(value) => formik.setFieldValue("address.province", value)}
          onBlur={() => formik.setFieldTouched("address.province", true)}
          error={getProfileFieldError({
            formik,
            name: "address.province",
          })}
        />
        <ProfileTextField
          formik={formik}
          name="address.postalCode"
          label="Postal/Zip Code"
          placeholder="Enter Postal/Zip Code"
        />
      </div>
    </SectionShell>

    <SectionShell
      id="social-links"
      title="Links to Socials"
      variant="overlapPill"
      description={`Enter and update your ${noun.singular} links to socials which will appear on your ${noun.singular} profile on the Chamber Perks App™.`}
    >
      <div className={twoColumnGridClassName}>
        {socialFields.map(([name, label, placeholder]) => (
          <ProfileTextField
            key={name}
            formik={formik}
            name={name}
            label={label}
            placeholder={placeholder}
          />
        ))}
      </div>
    </SectionShell>

    <SectionShell
      id="my-chamber-tab"
      title="My Chamber Tab"
      variant="overlapPill"
      description={`Add your ${noun.singular} URL links to appear in the My Chamber Tab of the Chamber Perks App™ and make it easy for users to connect, engage, and discover what your ${noun.singular} offers. Leave blank whatever links you do not want to appear; this section can be updated anytime.`}
    >
      <div className={twoColumnGridClassName}>
        {chamberTabFields.map(([name, label, placeholder]) => (
          <ProfileTextField
            key={name}
            formik={formik}
            name={name}
            label={label}
            placeholder={placeholder}
          />
        ))}
      </div>
    </SectionShell>

    <SectionShell
      id="sponsor-details"
      title="Chamber Perks App™ Sponsor Details"
      variant="overlapPill"
      description={
        <>
          <span className="block">
            Enter the member company logo and website URL that will appear in
            the app under your {noun.singular} profile and on the main directory page.
            The sponsor&apos;s logo will also be displayed on every
            member&apos;s dashboard. Your {noun.singular} should activate a Featured
            Member upgrade to provide additional exposure through profile and
            perk banners. This sponsorship opportunity allows your {noun.singular} to
            generate non-dues revenue. Your {noun.singular} may set its own sponsorship
            fee.
          </span>
          <span className="mt-2 block">
            Chamber Member Pro™ Platform will also have the Sponsorship Logo
            appear on the Chamber Directory on their website
          </span>
        </>
      }
    >
      <div className={twoColumnGridClassName}>
        <div className="min-w-0">
          <p className="mb-2 text-base font-bold text-black">Sponsor Logo</p>
          <p className="mb-[10px] text-sm text-black">
            Ideally a square PNG or JPG image 5MB in size
          </p>
          <LogoPicker
            logo={formik.values.sponsorLogo}
            label="Sponsor Logo"
            deleteLogo={onSponsorLogoDelete}
            base64={onSponsorLogoUpload}
          />
        </div>
        <ProfileTextField
          formik={formik}
          name="sponsorLogoLink"
          label="Sponsor Logo Link"
          description="Enter Sponsor website link to your sponsor logo image"
          placeholder="Enter Website URL"
        />
      </div>
    </SectionShell>

    <SectionShell
      id="profile-banners"
      title={`${noun.Singular} Profile Banners`}
      variant="overlapPill"
      className="min-[601px]:min-h-[514.5px]"
      description={`Images and video banners uploaded here will appear on your ${noun.singular} profile on the Chamber Perks App™. Use these banners to showcase your ${noun.singular}, events, and initiatives.`}
    >
      <p className="mb-[15px] text-sm leading-5 text-black sm:text-base sm:leading-6">
        Upload up to 4 image or video banners to promote your {noun.singular}. An aspect
        ratio of 16:9 and a minimum size of 1280x720 are recommended for best
        quality.
      </p>
      <BannerPicker
        formikVal={formik.values.profileBanner || []}
        formikSetFieldValue={(banners) =>
          formik.setFieldValue("profileBanner", banners)
        }
        pickerLabel={`${noun.Singular} Profile Banners`}
      />
    </SectionShell>

    <SectionShell
      id="directory-banners"
      title="Main Directory Banners"
      variant="overlapPill"
      className="min-[601px]:min-h-[514.5px]"
      description={
        <>
          <span className="block">
            Images and video banners uploaded here will appear on the Main
            Directory Banner in the Chamber Perks App™. Use these banners to
            showcase your {noun.singular}, events, and initiatives.
          </span>
          <span className="mt-2 block">
            Chambers using the Chamber Member Pro™ Platform will also benefit
            from having their banners appear in the Chamber Directory on their
            website.
          </span>
        </>
      }
    >
      <p className="mb-[15px] text-sm leading-5 text-black sm:text-base sm:leading-6">
        Upload up to 4 images or video banners to promote your profile. An
        aspect ratio of 16:9 and a minimum size of 1280x720 are recommended for
        best quality.
      </p>
      <BannerPicker
        formikVal={formik.values.directoryBanner || []}
        formikSetFieldValue={(banners) =>
          formik.setFieldValue("directoryBanner", banners)
        }
        pickerLabel="Main Directory Banners"
      />
    </SectionShell>
  </div>
  );
};

export default ManageChamberProfileSections;
